import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import BrandLogo from '../BrandLogo.jsx';
import BrandName from './BrandName.jsx';
import { BRAND_SHORT_NAME } from '../../constants/brand.js';
import { clsx } from '../../utils/cn.js';
import { PUBLIC_NAV_ITEMS } from '../../constants/navigation.js';
import { ASSOCIATE_PORTAL_URL } from '../../constants/associatePortal.js';
import {
  PSE_LOGO_SRC,
  PSE_PAYMENTS_HREF,
  PSE_PAYMENTS_IS_EXTERNAL,
  PSE_PAYMENTS_LABEL,
} from '../../constants/psePayments.js';

const navLinkClass = ({ isActive }) =>
  clsx(
    'block rounded-lg px-3 py-2 text-base font-medium transition',
    isActive
      ? 'bg-[#1acb12]/18 text-black ring-1 ring-[#0d2b7f]/35'
      : 'text-black/80 hover:bg-slate-100 hover:text-black'
  );

const dropdownItemClass = ({ isActive }) =>
  clsx(
    'block rounded-lg px-3 py-2.5 text-base font-medium transition',
    isActive
      ? 'bg-[#1acb12]/15 text-black'
      : 'text-black/85 hover:bg-slate-50 hover:text-black'
  );

function isDropdownSectionActive(items, pathname) {
  return items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}

function NavDropdownDesktop({ config }) {
  const location = useLocation();
  const menuId = useId();
  const rootRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const active = isDropdownSectionActive(config.items, location.pathname);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        className={clsx(
          'inline-flex items-center gap-1 rounded-lg px-3 py-2 text-base font-medium text-black transition',
          (active || menuOpen) &&
            'bg-[#1acb12]/18 text-black ring-1 ring-[#0d2b7f]/35',
          !active && !menuOpen && 'text-black/80 hover:bg-slate-100 hover:text-black'
        )}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={menuId}
      >
        {config.label}
        <ChevronDown
          className={clsx('h-4 w-4 shrink-0 transition duration-200', menuOpen && 'rotate-180')}
          aria-hidden
        />
      </button>
      <div
        id={menuId}
        role="menu"
        className={clsx(
          'absolute left-0 top-full z-50 min-w-[13.5rem] pt-2 transition-[opacity,visibility] duration-150',
          menuOpen ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'
        )}
      >
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-black/5">
          {config.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              role="menuitem"
              className={dropdownItemClass}
              onClick={() => setMenuOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

function NavbarPseLink({ className, onNavigate, showLabel = false }) {
  const linkClass = clsx(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-[#1acb12]/45 bg-[#1acb12]/10 px-2 py-1.5 transition hover:bg-[#1acb12]/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1acb12]',
    className
  );
  const ariaLabel = PSE_PAYMENTS_IS_EXTERNAL
    ? `${PSE_PAYMENTS_LABEL}: abrir portal de pagos en una nueva pestaña`
    : `${PSE_PAYMENTS_LABEL}: información de pagos`;

  const content = (
    <>
      <img
        src={PSE_LOGO_SRC}
        alt=""
        className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-black/10"
        width={32}
        height={32}
        loading="lazy"
        decoding="async"
      />
      {showLabel ? (
        <span className="hidden text-sm font-semibold uppercase tracking-wide text-black lg:inline">
          {PSE_PAYMENTS_LABEL}
        </span>
      ) : null}
    </>
  );

  if (PSE_PAYMENTS_IS_EXTERNAL) {
    return (
      <a
        href={PSE_PAYMENTS_HREF}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
        aria-label={ariaLabel}
        onClick={onNavigate}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={PSE_PAYMENTS_HREF} className={linkClass} aria-label={ariaLabel} onClick={onNavigate}>
      {content}
    </Link>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      setOpen(false);
      setMobileDropdown(null);
      prevPath.current = location.pathname;
    }
  }, [location.pathname]);

  const ingresoClassName =
    'inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-black px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-white shadow-sm transition-all hover:bg-neutral-900 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-400';

  return (
    <header className="sticky top-0 z-40 border-b-[3px] border-[#1acb12] bg-white/95 shadow-sm backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 lg:px-8 lg:py-2.5">
        <Link
          to="/"
          className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-3 lg:max-w-none"
        >
          <BrandLogo
            alt={BRAND_SHORT_NAME}
            eager
            className="h-7 w-[5.5rem] max-h-7 max-w-[5.5rem] shrink-0 lg:h-10 lg:w-auto lg:max-h-10 lg:max-w-[10.5rem]"
          />
          <BrandName
            variant="onLight"
            className="block md:hidden lg:block"
            legalClassName="hidden lg:block text-xs sm:text-sm"
            shortNameClassName="text-base sm:text-lg"
          />
        </Link>

        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-1 md:flex">
          {PUBLIC_NAV_ITEMS.map((item) =>
            item.type === 'dropdown' ? (
              <NavDropdownDesktop key={item.label} config={item} />
            ) : (
              <NavLink key={item.to} to={item.to} className={navLinkClass}>
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          <NavbarPseLink showLabel className="hidden md:inline-flex" />
          <NavbarPseLink className="md:hidden" onNavigate={() => setOpen(false)} />

          {/* <a
            href={ASSOCIATE_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(ingresoClassName, 'hidden md:inline-flex')}
          >
            Ingreso
          </a> */}
        </div>

        <button
          type="button"
          className="inline-flex shrink-0 rounded-lg p-2 text-black hover:bg-slate-100 md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label="Menú"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {PUBLIC_NAV_ITEMS.map((item) =>
              item.type === 'dropdown' ? (
                <div key={item.label}>
                  <button
                    type="button"
                    onClick={() =>
                      setMobileDropdown((current) => (current === item.label ? null : item.label))
                    }
                    className={clsx(
                      'flex w-full items-center justify-between rounded-lg px-3 py-2 text-base font-medium text-black',
                      isDropdownSectionActive(item.items, location.pathname) && 'bg-[#1acb12]/12'
                    )}
                    aria-expanded={mobileDropdown === item.label}
                  >
                    {item.label}
                    <ChevronDown
                      className={clsx(
                        'h-4 w-4 transition',
                        mobileDropdown === item.label && 'rotate-180'
                      )}
                      aria-hidden
                    />
                  </button>
                  {mobileDropdown === item.label && (
                    <div className="ml-3 mt-1 flex flex-col gap-0.5 border-l-2 border-[#1acb12]/35 pl-3">
                      {item.items.map((sub) => (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          onClick={() => {
                            setOpen(false);
                            setMobileDropdown(null);
                          }}
                          className={clsx(
                            'rounded-lg px-3 py-2 text-base text-black/85 hover:bg-slate-100',
                            (location.pathname === sub.to ||
                              location.pathname.startsWith(`${sub.to}/`)) &&
                              'bg-[#1acb12]/15 font-medium text-black'
                          )}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={clsx(
                    'rounded-lg px-3 py-2 text-base text-black hover:bg-slate-100',
                    location.pathname === item.to && 'bg-[#1acb12]/15 font-medium'
                  )}
                >
                  {item.label}
                </Link>
              )
            )}
            {/* <a
              href={ASSOCIATE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className={clsx(ingresoClassName, 'mt-2 w-full')}
            >
              Ingreso
            </a> */}
          </div>
        </div>
      )}
    </header>
  );
}
